import React, { useEffect, useMemo, useState } from 'react';
import { X, Clock, Brain } from 'lucide-react';
import { getActiveTrivia, getPreviewTrivia, getTriviaWindow } from '../utils/specialEventUtils';

/**
 * TriviaBanner — a floating "ticket" that slides in top-right on trivia
 * night (clear of the navbar and its round logo), instead of a full-width
 * strip over the hero.
 *
 *   expanded  → headline, live countdown, "Save me a seat" CTA, confetti
 *   chip      → after EXPAND_MS it folds into a small persistent pill that
 *               keeps reminding without obscuring; tap to re-open
 *   gone      → dismissed, or the trivia window has ended
 *
 * Preview any day with ?trivia in the URL.
 */

const EXPAND_MS = 10000;   // how long the full card stays before folding
const EXIT_MS = 480;       // exit/fold transition length — matches CSS below
const TICK_MS = 30000;     // countdown refresh

const CONFETTI_COLORS = [
  '#C9A227', '#D4A574', '#E8C766', '#FDF6E9',
  '#E63946', '#2A9D8F', '#F4A261', '#9B5DE5',
];

function buildConfetti(count) {
  return Array.from({ length: count }, (_, i) => {
    const isCircle = Math.random() > 0.55;
    const size = 5 + Math.random() * 6;
    return {
      id: i,
      left: Math.random() * 100,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      width: isCircle ? size : size * (0.5 + Math.random() * 0.5),
      height: size,
      radius: isCircle ? '50%' : '1px',
      drift: `${Math.round(-40 + Math.random() * 80)}px`,
      spin: `${Math.round(180 + Math.random() * 540)}deg`,
      duration: `${(1.8 + Math.random() * 1.4).toFixed(2)}s`,
      delay: `${(0.25 + Math.random() * 0.6).toFixed(2)}s`,
    };
  });
}

function formatCountdown(ms) {
  const totalMin = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function clockLabel(date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(':00', '');
}

/**
 * "Starts in 1h 52m" / "Happening now · until 8:30" / "This Thursday"
 */
function statusLine(trivia) {
  if (trivia.daysUntil > 0) {
    return trivia.daysUntil === 1 ? 'Tomorrow night' : `This ${trivia.dayName}`;
  }
  const { start, end } = getTriviaWindow(trivia);
  const now = Date.now();
  if (now < start.getTime()) return `Starts in ${formatCountdown(start.getTime() - now)}`;
  if (now < end.getTime()) return `Happening now · until ${clockLabel(end)}`;
  return null; // over
}

/**
 * Preview mode (?trivia) must always render something, even after tonight's
 * window has passed — designers check this at all hours. Falls back to a
 * "Tonight at 7 PM" style label instead of hiding.
 */
function previewStatus(trivia) {
  return statusLine(trivia) || `Tonight at ${clockLabel(getTriviaWindow(trivia).start)}`;
}

const TriviaBanner = () => {
  const [trivia, setTrivia] = useState(null);
  const [phase, setPhase] = useState('hidden'); // hidden | expanded | folding | chip | leaving
  const [status, setStatus] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  const confetti = useMemo(() => buildConfetti(42), []);

  // Resolve tonight's trivia (or the ?trivia preview) once on mount.
  useEffect(() => {
    const preview =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).has('trivia');

    const active = preview ? getPreviewTrivia() : getActiveTrivia();
    if (!active) return;

    const initial = preview ? previewStatus(active) : statusLine(active);
    if (!initial) return; // already over tonight

    setIsPreview(preview);
    setTrivia(active);
    setStatus(initial);

    // Seen this session already → go straight to the quiet chip.
    const seenKey = `trivia-banner-seen-${active.date || 'weekly'}`;
    let seen = false;
    try { seen = !preview && !!sessionStorage.getItem(seenKey); } catch (_) {}
    setPhase(seen ? 'chip' : 'expanded');
    try { sessionStorage.setItem(seenKey, '1'); } catch (_) {}
  }, []);

  // Fold the expanded card into the chip after EXPAND_MS.
  useEffect(() => {
    if (phase !== 'expanded') return;
    const t1 = setTimeout(() => setPhase('folding'), EXPAND_MS);
    return () => clearTimeout(t1);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'folding') return;
    const t = setTimeout(() => setPhase('chip'), EXIT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Live countdown; hides everything once the window has ended.
  useEffect(() => {
    if (!trivia || phase === 'hidden' || phase === 'leaving') return;
    const tick = () => {
      if (isPreview) { setStatus(previewStatus(trivia)); return; } // never auto-hide a preview
      const s = statusLine(trivia);
      if (!s) { setPhase((p) => (p === 'chip' ? 'hidden' : 'leaving')); return; }
      setStatus(s);
    };
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [trivia, phase, isPreview]);

  useEffect(() => {
    if (phase !== 'leaving') return;
    const t = setTimeout(() => setPhase('hidden'), EXIT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // The chip is small enough to just vanish; the card gets its exit animation.
  const dismiss = () => setPhase((p) => (p === 'chip' ? 'hidden' : 'leaving'));

  if (phase === 'hidden' || !trivia) return null;

  const isLive = status.startsWith('Happening');
  const showCard = phase === 'expanded' || phase === 'folding' || (phase === 'leaving' && status);
  const cardOut = phase === 'folding' || phase === 'leaving';

  return (
    <div
      // Below the 64px navbar, right-aligned so it clears the round logo
      // that overhangs the bar on the left. Above the nav in stacking so
      // nothing paints over it; the nav itself stays fully usable.
      className="fixed top-[76px] right-3 sm:right-6 flex justify-end pointer-events-none"
      style={{ zIndex: 80 }}
      role="status"
      aria-live="polite"
    >
      <style>{TRIVIA_KEYFRAMES}</style>

      {/* ── Expanded ticket ─────────────────────────────────────────── */}
      {showCard && phase !== 'chip' && (
        <div
          className="relative pointer-events-auto overflow-hidden rounded-md"
          style={{
            width: 'min(340px, calc(100vw - 150px))',
            background: 'linear-gradient(160deg, #2C2416 0%, #1a1612 70%)',
            border: '1px solid rgba(201,162,39,0.55)',
            boxShadow: '0 18px 40px -12px rgba(0,0,0,0.65), 0 0 0 1px rgba(0,0,0,0.4)',
            animation: cardOut
              ? `trivia-card-out ${EXIT_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
              : 'trivia-card-in 0.7s cubic-bezier(0.18, 0.89, 0.32, 1.22) both, trivia-float 4.5s ease-in-out 0.7s infinite',
          }}
        >
          {/* gold ticket edge */}
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-[#E8C766] via-[#C9A227] to-[#8A6F1C]" />

          {/* spotlight */}
          <div
            className="absolute -top-10 left-1/2 -translate-x-1/2 pointer-events-none"
            style={{ width: 320, height: 160, background: 'radial-gradient(ellipse at center, rgba(201,162,39,0.28), transparent 70%)' }}
          />

          {/* confetti, contained to the card */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {confetti.map((c) => (
              <span
                key={c.id}
                style={{
                  position: 'absolute',
                  top: '-12px',
                  left: `${c.left}%`,
                  width: `${c.width}px`,
                  height: `${c.height}px`,
                  background: c.color,
                  borderRadius: c.radius,
                  opacity: 0,
                  '--drift': c.drift,
                  '--spin': c.spin,
                  animation: `trivia-confetti-fall ${c.duration} linear ${c.delay} forwards`,
                }}
              />
            ))}
          </div>

          <div className="relative pl-5 pr-3 pt-3 pb-3.5">
            {/* eyebrow row */}
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500' : 'bg-[#C9A227]'}`} style={{ animation: 'trivia-pulse 1.6s ease-in-out infinite' }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4A574] whitespace-nowrap">
                {isLive ? 'Live now' : 'Tonight'}<span className="hidden sm:inline"> · Team Trivia</span>
              </span>
              <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="ml-auto -mr-1 p-2 text-[#FDF6E9]/45 hover:text-[#FDF6E9] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* headline */}
            <p
              className="font-display leading-none tracking-wide text-2xl sm:text-3xl"
              style={{
                background: 'linear-gradient(90deg, #F4E3A1 0%, #C9A227 25%, #FDF6E9 50%, #C9A227 75%, #F4E3A1 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'trivia-shimmer 2.6s linear infinite',
                filter: 'drop-shadow(0 1px 6px rgba(201,162,39,0.35))',
              }}
            >
              {trivia.headline}
            </p>

            {/* countdown */}
            <div className="mt-2 flex items-center gap-1.5 text-[13px] text-[#FDF6E9]/80" style={{ fontVariantNumeric: 'tabular-nums' }}>
              <Clock className="w-3.5 h-3.5 text-[#C9A227] shrink-0" />
              <span key={status} style={{ animation: 'trivia-tick 0.5s ease-out both' }}>{status}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Folded chip ─────────────────────────────────────────────── */}
      {phase === 'chip' && (
        <div
          className="relative pointer-events-auto flex items-center gap-1 rounded-full pl-3 pr-1.5 py-1.5"
          style={{
            background: 'linear-gradient(160deg, #2C2416 0%, #1a1612 70%)',
            border: '1px solid rgba(201,162,39,0.55)',
            boxShadow: '0 10px 24px -10px rgba(0,0,0,0.6)',
            animation: 'trivia-chip-in 0.45s cubic-bezier(0.18, 0.89, 0.32, 1.28) both',
          }}
        >
          <button
            onClick={() => setPhase('expanded')}
            className="flex items-center gap-2 text-left"
            aria-label="Show trivia details"
          >
            <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500' : 'bg-[#C9A227]'}`} style={{ animation: 'trivia-pulse 1.6s ease-in-out infinite' }} />
            <Brain className="w-4 h-4 text-[#C9A227]" />
            <span className="font-display text-base tracking-wide text-[#FDF6E9] leading-none pt-px">
              {isLive ? 'TRIVIA LIVE' : `TRIVIA ${trivia.time ? '@ ' + trivia.time.replace(/:00/, '').replace(/[AP]M/i, '') : 'TONIGHT'}`}
            </span>
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="p-1.5 text-[#FDF6E9]/45 hover:text-[#FDF6E9] transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

const TRIVIA_KEYFRAMES = `
  @keyframes trivia-card-in {
    0%   { transform: translateX(120%) rotate(2deg); opacity: 0; }
    100% { transform: translateX(0) rotate(0deg); opacity: 1; }
  }
  @keyframes trivia-card-out {
    0%   { transform: translateY(0) scale(1); opacity: 1; }
    100% { transform: translateY(-12px) scale(0.92); opacity: 0; }
  }
  @keyframes trivia-float {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-3px); }
  }
  @keyframes trivia-chip-in {
    0%   { transform: scale(0.6); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes trivia-confetti-fall {
    0%   { transform: translate3d(0, -12px, 0) rotateZ(0deg); opacity: 0; }
    8%   { opacity: 1; }
    100% { transform: translate3d(var(--drift), 180px, 0) rotateZ(var(--spin)); opacity: 0; }
  }
  @keyframes trivia-pulse {
    0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(201,162,39,0.5); }
    50%      { transform: scale(1.15); opacity: 0.85; box-shadow: 0 0 0 5px rgba(201,162,39,0); }
  }
  @keyframes trivia-tick {
    0%   { transform: translateY(4px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes trivia-shimmer {
    0%   { background-position: 0% center; }
    100% { background-position: 200% center; }
  }
  @media (prefers-reduced-motion: reduce) {
    [style*="trivia-"] { animation: none !important; }
  }
`;

export default TriviaBanner;
